import type { ReactNode } from 'react'

/**
 * Cabecalho de pagina: titulo grande + uma linha do que a tela faz.
 *
 * Existe para responder "onde eu estou e para que serve isto" sem o usuario
 * ter que adivinhar pela lista de paineis — foi o "ruim de me achar".
 */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-ink-3">{subtitle}</p>}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  )
}

export function Panel({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-panel shadow-sm">
      {title && (
        <header className="flex items-center justify-between gap-2 border-b border-line px-5 py-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-3">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  )
}

const PILL: Record<string, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  crit: 'bg-crit-soft text-crit',
  neutral: 'bg-ground text-ink-3',
  accent: 'bg-accent-soft text-accent',
}

export function Pill({ tone = 'neutral', children }: { tone?: keyof typeof PILL; children: ReactNode }) {
  return (
    <span
      className={`inline-block rounded-sm px-2 py-[2px] font-mono text-[10px] font-bold uppercase tracking-wider ${PILL[tone]}`}
    >
      {children}
    </span>
  )
}

/** Estado de energia com cor semantica — legivel de relance na lista. */
export function PowerPill({ state }: { state: string }) {
  const tone = state === 'on' ? 'ok' : state === 'off' ? 'crit' : state === 'transitioning' ? 'warn' : 'neutral'
  const label =
    state === 'on' ? 'ligado' : state === 'off' ? 'desligado' : state === 'transitioning' ? 'mudando' : 'desconhecido'
  return <Pill tone={tone as keyof typeof PILL}>{label}</Pill>
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  disabled?: boolean
  type?: 'button' | 'submit'
  title?: string
}) {
  const styles = {
    default: 'border-line bg-panel text-ink hover:border-accent hover:text-accent',
    primary: 'border-accent bg-accent text-white hover:opacity-90',
    danger: 'border-crit bg-crit-soft text-crit hover:bg-crit hover:text-white',
    ghost: 'border-transparent bg-transparent text-ink-3 hover:text-ink',
  }[variant]
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${styles}`}
    >
      {children}
    </button>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-3">{hint}</span>}
    </label>
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none ${props.className ?? ''}`}
    />
  )
}

export function Alert({ tone = 'crit', children }: { tone?: 'crit' | 'warn' | 'ok'; children: ReactNode }) {
  const map = {
    crit: 'border-l-crit bg-crit-soft text-crit',
    warn: 'border-l-warn bg-warn-soft text-warn',
    ok: 'border-l-ok bg-ok-soft text-ok',
  }[tone]
  return <div className={`border-l-[3px] px-4 py-3 text-sm ${map}`}>{children}</div>
}

/**
 * Medidor de porcentagem — barra com cor por faixa.
 *
 * A cor conta a historia de relance: verde tranquilo, ambar atencao, vermelho
 * age agora. As faixas (70/90) sao as mesmas do motor de alertas, para a tela
 * e o alerta nunca contarem coisas diferentes.
 */
export function Medidor({ label, valor, sufixo = '%' }: { label: string; valor: number | null; sufixo?: string }) {
  const v = valor ?? 0
  const tom = v >= 90 ? 'var(--color-crit)' : v >= 70 ? 'var(--color-warn)' : 'var(--color-ok)'
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-mono uppercase tracking-wider text-ink-3">{label}</span>
        <span className="font-mono text-ink">{valor === null ? '—' : `${v}${sufixo}`}</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-sm bg-ground">
        <div
          className="h-full rounded-sm transition-[width]"
          style={{ width: `${Math.min(100, Math.max(0, v))}%`, background: tom }}
        />
      </div>
    </div>
  )
}

/**
 * Sparkline em SVG puro — sem lib de grafico, como o projeto pede.
 *
 * Escala fixa 0–100 de proposito: e porcentagem, e uma escala que se ajusta
 * sozinha ao maximo da janela faria 40% parecer pico num dia calmo. Ponto
 * ausente (agente offline num trecho) quebra a linha em vez de interpolar uma
 * reta que nao aconteceu.
 */
export function Sparkline({
  pontos,
  cor = 'var(--color-accent)',
  altura = 40,
}: {
  pontos: Array<number | null>
  cor?: string
  altura?: number
}) {
  const L = 240
  const n = pontos.length
  if (n === 0) return <div className="text-xs text-ink-3">sem dados no periodo</div>

  const x = (i: number) => (n === 1 ? 0 : (i / (n - 1)) * L)
  const y = (v: number) => altura - (Math.min(100, Math.max(0, v)) / 100) * altura

  // Segmentos separados por buracos (null) — cada corrida vira um path.
  const segmentos: string[] = []
  let atual: string[] = []
  pontos.forEach((p, i) => {
    if (p === null) {
      if (atual.length) segmentos.push(atual.join(' '))
      atual = []
    } else {
      atual.push(`${atual.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(p).toFixed(1)}`)
    }
  })
  if (atual.length) segmentos.push(atual.join(' '))

  return (
    <svg viewBox={`0 0 ${L} ${altura}`} className="w-full" preserveAspectRatio="none" style={{ height: altura }}>
      <line x1="0" y1={y(90)} x2={L} y2={y(90)} stroke="var(--color-line)" strokeWidth="0.5" strokeDasharray="3 3" />
      {segmentos.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={cor} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  )
}

/** Formata bytes para leitura humana. */
export function bytesHumano(n: number | string | null): string {
  const v = typeof n === 'string' ? Number(n) : (n ?? 0)
  if (!v) return '—'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let x = v
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024
    i++
  }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}

/** Uptime em texto curto: "3d 4h", "5h 12m", "8m". */
export function uptimeHumano(seg: number | null): string {
  if (!seg) return '—'
  const d = Math.floor(seg / 86400)
  const h = Math.floor((seg % 86400) / 3600)
  const m = Math.floor((seg % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}
